import {
  AccommodationArrangedStatusEnum,
  CoreStatusExtended,
  EventBookingStatus,
  ICalendlyEvent,
  ISummaryRecordedInterview,
  RecordedInterviewSystemStatus,
  SectionStatusType,
  TravelStatusEnum,
  VisaStatus
} from 'types';
import { useSelector } from 'react-redux';
import { offerVisaSupport, hasAgentExcludingEnroly, isTrackerVisible } from 'shared-components';

import { ApplicationState } from '../../../store';
import { EnrolmentStatusEnum } from '../../../store/student/offer/types';
import { hasAnyMatchingFinDocs, hasDocumentInError } from '../../../utilities/documents';
import { getEarliestCourseStartDate } from '../../../utilities/getEarliestCourseStartDate';
import { Status, ISectionProps, SectionId } from '../../molecules/Sidebar/types';
import { IOffer } from 'frontend/student/src/types';

const hasScheduledInterviews = (
  calendlyInterviews: ICalendlyEvent[],
  recordedInterviewStatus: RecordedInterviewSystemStatus
) => {
  return (
    recordedInterviewStatus !== RecordedInterviewSystemStatus.NONE ||
    (calendlyInterviews.length !== 0 &&
      calendlyInterviews.every((interview: ICalendlyEvent) => {
        return Object.entries(interview.scheduledEvents ?? {}).length !== 0;
      }))
  );
};

const getHighestPriorityEnrolmentStatus = (
  statuses: {
    travelStatus?: TravelStatusEnum;
    visaStatus?: VisaStatus;
    accommodationArrangedStatus?: AccommodationArrangedStatusEnum;
  },
  offerStatus: CoreStatusExtended
): EnrolmentStatusEnum => {
  const isTravelSectionVisible = offerStatus === CoreStatusExtended.VISA_ISSUED;

  const travelStatus = statuses.travelStatus || TravelStatusEnum.None;
  const visaStatus = statuses.visaStatus || VisaStatus.NONE;
  const accommodationArrangedStatus = statuses.accommodationArrangedStatus || AccommodationArrangedStatusEnum.NONE;

  if (
    (isTravelSectionVisible && travelStatus === TravelStatusEnum.None) ||
    visaStatus === VisaStatus.NONE ||
    accommodationArrangedStatus === AccommodationArrangedStatusEnum.NONE
  ) {
    return EnrolmentStatusEnum.None;
  }

  if (
    (isTravelSectionVisible &&
      (travelStatus === TravelStatusEnum.TravelPrevented ||
        travelStatus === TravelStatusEnum.TravelCancelled ||
        travelStatus === TravelStatusEnum.NoBookingYet)) ||
    visaStatus === VisaStatus.UKVI_REQUEST_MORE_INFO ||
    visaStatus === VisaStatus.CAS_NOT_RECEIVED ||
    visaStatus === VisaStatus.CAS_RECEIVED ||
    accommodationArrangedStatus === AccommodationArrangedStatusEnum.NOT_ARRANGED
  ) {
    return EnrolmentStatusEnum.Question;
  }

  if (
    (isTravelSectionVisible && travelStatus === TravelStatusEnum.Booked) ||
    visaStatus === VisaStatus.AWAITING_VISA_RESPONSE ||
    visaStatus === VisaStatus.VISA_APPLIED
  ) {
    return EnrolmentStatusEnum.Wait;
  }

  if (
    (isTravelSectionVisible && travelStatus === TravelStatusEnum.Withdraw) ||
    visaStatus === VisaStatus.INTENDS_TO_WITHDRAW
  ) {
    return EnrolmentStatusEnum.Withdraw;
  }

  if (
    (isTravelSectionVisible && travelStatus === TravelStatusEnum.Defer) ||
    visaStatus === VisaStatus.INTENDS_TO_DEFER
  ) {
    return EnrolmentStatusEnum.Defer;
  }

  if (visaStatus === VisaStatus.VISA_REFUSAL) {
    return EnrolmentStatusEnum.Cancel;
  }

  if (
    (isTravelSectionVisible &&
      (travelStatus === TravelStatusEnum.Arrived || travelStatus === TravelStatusEnum.AlreadyInUk)) ||
    visaStatus === VisaStatus.VISA_RECEIVED ||
    accommodationArrangedStatus === AccommodationArrangedStatusEnum.ARRANGED
  ) {
    return EnrolmentStatusEnum.OK;
  }

  return EnrolmentStatusEnum.None;
};

const SHOW_ENROLMENT_SECTION_STATUSES = [
  CoreStatusExtended.CAS_ISSUED,
  CoreStatusExtended.VISA_ISSUED,
  CoreStatusExtended.VISA_REFUSED
];

export const useDashboardData = () => {
  const student = useSelector((state: ApplicationState) => state.student);
  const ui = useSelector((state: ApplicationState) => state.ui);
  const config = useSelector((state: ApplicationState) => state.config);
  const data = useSelector((state: ApplicationState) => state.data);

  const casBypass = student.offer?.casBypass || false;

  const historyInactive = config.historySectionStatus === SectionStatusType.INACTIVE;
  const financeInactive = config.financeSectionStatus === SectionStatusType.INACTIVE;

  const isChecklistComplete =
    (student.offer.isPISectionComplete || !student.summary.studentDetailsRequired) &&
    (student.offer.isHSectionComplete || !student.summary.historyRequired || historyInactive) &&
    (student.consents.isSectionComplete || !student.summary.consentsRequired) &&
    (student.financials.isSectionComplete || !student.summary.financeRequired || financeInactive);

  const isChecklistInProgress =
    student.offer.isPISectionStarted ||
    student.offer.isHSectionStarted ||
    student.consents.isSectionStarted ||
    !!student.financials.isSectionStarted;
  const isChecklistHidden = casBypass;

  const isDocSectionComplete = student.summary.isDocsComplete;
  const isDocSectionInProgress = student.summary.isDocsStarted;

  const finDocs = student.financeDocuments;

  const isFinanceDocComplete = student.summary.isFinDocsComplete;
  const isFinanceDocInProgress = student.summary.isFinDocsStarted;
  const isFinanceDocInvalid = hasAnyMatchingFinDocs(finDocs, 'UPLOADED_WITH_ERROR', hasDocumentInError);

  const isPreCasInterviewComplete = student.preCasInterview.isSectionComplete;
  const isPreCasInterviewStarted = student.preCasInterview.isSectionStarted;

  const isPreCasFormHidden = config?.interviewSectionStatus === SectionStatusType.INACTIVE || casBypass;

  const enrolmentStatus = getHighestPriorityEnrolmentStatus(
    {
      travelStatus: student.enrolmentStatus?.travelStatusSection?.status,
      visaStatus: student.offer.visaStatus,
      accommodationArrangedStatus: student.enrolmentStatus?.accommodationStatusSection?.arrangedStatus
    },
    student.offer.status
  );

  const isEnrolmentFormHidden = !SHOW_ENROLMENT_SECTION_STATUSES.includes(student.offer.status);

  const isBeforeTravelDocsComplete = student.summary.isBeforeTravelDocsSectionComplete;
  const isBeforeTravelDocsInProgress = student.summary.isBeforeTravelDocsSectionStarted;
  const isAfterArrivalDocsComplete = student.summary.isAfterArrivalDocsSectionComplete;
  const isAfterArrivalDocsInProgress = student.summary.isAfterArrivalDocsSectionStarted;

  const isVisaPrepInProgress = student.visa.isSectionStarted;
  const isVisaPrepComplete = !!student.visa.isSectionComplete;

  const studentHasAgent = hasAgentExcludingEnroly(student.personalInfo, student.importData, student.offer as IOffer);
  const isVisaPrepFormHidden = !offerVisaSupport(student.offer as IOffer, config, casBypass, studentHasAgent);
  const isVisaPrepFormActivated = student.visa.hasBeenActivated;

  const isCASIssued = student.offer.status === CoreStatusExtended.CAS_ISSUED;

  const isGeneralDocFormHidden = config?.docSectionStatus === SectionStatusType.INACTIVE || casBypass;
  const isFinanceDocFormHidden = config?.finDocSectionStatus === SectionStatusType.INACTIVE || casBypass;

  const isPreDepartureDocSectionHidden =
    config?.preDepartureDocSectionStatus === SectionStatusType.INACTIVE ||
    !(
      [CoreStatusExtended.CAS_ISSUED, CoreStatusExtended.VISA_ISSUED, CoreStatusExtended.VISA_REFUSED].includes(
        student.offer.status!
      ) || student.summary.isBeforeTravelDocsSectionStarted
    );
  const isAfterTravelDocSectionHidden =
    config?.afterTravelDocSectionStatus === SectionStatusType.INACTIVE ||
    !(
      [CoreStatusExtended.CAS_ISSUED, CoreStatusExtended.VISA_ISSUED, CoreStatusExtended.VISA_REFUSED].includes(
        student.offer.status!
      ) || student.summary.isAfterArrivalDocsSectionStarted
    );

  const calendlyInterviews: ICalendlyEvent[] = student.offer.calendlyInterviews ?? [];
  const recordedInterviewSystemStatus = student.offer.recordedInterviewSystemStatus;
  const recordedInterviewStudentStatus = student.offer.recordedInterviewStudentStatus;

  const isScheduledInterviewsHidden =
    (config?.scheduledInterviewsSectionStatus === SectionStatusType.INACTIVE &&
      !hasScheduledInterviews(calendlyInterviews, recordedInterviewSystemStatus)) ||
    !hasScheduledInterviews(calendlyInterviews, recordedInterviewSystemStatus) ||
    casBypass;

  const isScheduledInterviewsNew = calendlyInterviews.every(interview => {
    return Object.entries(interview.scheduledEvents ?? {}).every(([_key, event]) => {
      return [EventBookingStatus.REQUESTED].includes(event.bookingStatus);
    });
  });
  const isCalendlyInterviewsComplete = calendlyInterviews.every(interview => {
    return Object.entries(interview.scheduledEvents ?? {}).every(([_key, event]) => {
      return [EventBookingStatus.BOOKED, EventBookingStatus.CANCELED].includes(event.bookingStatus);
    });
  });

  const isRecordedInterviewsComplete = ![
    RecordedInterviewSystemStatus.INVITED,
    RecordedInterviewSystemStatus.ATTEMPTED
  ].includes(recordedInterviewStudentStatus || recordedInterviewSystemStatus);

  const isScheduledInterviewsComplete = isCalendlyInterviewsComplete && isRecordedInterviewsComplete;

  const sections: Array<ISectionProps> = getSections(
    isChecklistInProgress,
    isChecklistComplete === true,
    isChecklistHidden,
    isDocSectionInProgress,
    isDocSectionComplete,
    isFinanceDocInvalid,
    isFinanceDocInProgress,
    isFinanceDocComplete,
    isPreCasInterviewStarted === true,
    isPreCasInterviewComplete === true,
    isBeforeTravelDocsComplete === true,
    isBeforeTravelDocsInProgress === true,
    isAfterArrivalDocsComplete === true,
    isAfterArrivalDocsInProgress === true,
    isVisaPrepInProgress === true,
    isVisaPrepComplete,
    isVisaPrepFormHidden,
    isPreCasFormHidden,
    isGeneralDocFormHidden,
    isFinanceDocFormHidden,
    isPreDepartureDocSectionHidden,
    isAfterTravelDocSectionHidden,
    isVisaPrepFormActivated,
    isCASIssued,
    enrolmentStatus,
    isEnrolmentFormHidden,

    isScheduledInterviewsHidden,
    isScheduledInterviewsNew,
    isScheduledInterviewsComplete,

    ui.sections,
    student.offer.casBypass || false,
    student.offer.status
  );

  const getInitialActiveSectionId = () => {
    if (casBypass) {
      return SectionId.PRE_DEPARTURE_DOCS;
    }

    const sectionId = new URLSearchParams(window.location.search).get('sectionId');
    if (sectionId === SectionId.ENROLMENT && !isEnrolmentFormHidden) {
      return SectionId.ENROLMENT;
    }

    let initialSectionId = SectionId.CHECKLIST;
    if (!isChecklistComplete) {
      initialSectionId = SectionId.CHECKLIST;
    }
    if (isChecklistComplete && !isDocSectionComplete) {
      initialSectionId = SectionId.GENERAL_DOC;
    }
    if (isChecklistComplete && isDocSectionComplete && !isFinanceDocComplete) {
      initialSectionId = SectionId.FINANCE_DOC;
    }
    if (
      isChecklistComplete &&
      isDocSectionComplete &&
      isFinanceDocComplete &&
      !isPreCasInterviewComplete &&
      !isPreCasFormHidden
    ) {
      initialSectionId = SectionId.PRE_CAS_INTERVIEW;
    }
    if (
      !isVisaPrepFormHidden &&
      (isVisaPrepInProgress ||
        (isChecklistComplete &&
          isDocSectionComplete &&
          isFinanceDocComplete &&
          (isPreCasFormHidden || isPreCasInterviewComplete)))
    ) {
      initialSectionId = SectionId.VISA_PREP;
    }
    const initialSection = sections.find(section => section.id === initialSectionId);
    if (
      initialSection &&
      initialSection.status !== Status.Disabled &&
      initialSection.clickable === true &&
      initialSection.hidden !== true
    ) {
      return initialSectionId;
    }
    return SectionId.CHECKLIST;
  };

  const startDate = student.offer.courses ? getEarliestCourseStartDate(student.offer.courses) : null;
  const completeLabel = startDate ? startDate.format('Do of MMMM') : config.sections.Registration.label;

  const trackerVisible = isTrackerVisible(student.summary);

  return {
    sections,
    showCompleteModal:
      trackerVisible && (isVisaPrepFormHidden !== true && isVisaPrepFormActivated ? isVisaPrepComplete : true),
    openModal: ui.openModal,
    taskMenuOpen: ui.taskMenuOpen,
    // Show CAS Tracker right away, if student does not have visa support.
    activeSectionId: ui.activeSectionId,
    initialActiveSectionId: getInitialActiveSectionId(),
    completeLabel,
    completeSubLabel: config.sections.Registration.description,
    student: student,
    isStudentLoaded: data.isStudentLoaded,
    summary: student.summary,
    isTrackerVisible: trackerVisible,
    student_number: student.core.student_number,
    student_name: student.personalInfo.givenName
  };
};

const getSections = (
  isChecklistInProgress: boolean,
  isChecklistComplete: boolean,
  isChecklistHidden: boolean,
  isDocSectionInProgress: boolean,
  isDocSectionComplete: boolean,
  isFinanceDocInvalid: boolean,
  isFinanceDocInProgress: boolean,
  isFinanceDocComplete: boolean,
  isPreCasInterviewStarted: boolean,
  isPreCasInterviewComplete: boolean,
  isBeforeTravelDocsComplete: boolean,
  isBeforeTravelDocsInProgress: boolean,
  isAfterArrivalDocsComplete: boolean,
  isAfterArrivalDocsInProgress: boolean,
  isVisaPrepFormStarted: boolean,
  isVisaPrepFormComplete: boolean,
  isVisaPrepFormHidden: boolean,
  isPreCasFormHidden: boolean,
  isGeneralDocFormHidden: boolean,
  isFinanceDocFormHidden: boolean,
  isPreDepartureDocSectionHidden: boolean,
  isAfterTravelDocSectionHidden: boolean,
  isVisaPrepFormActivated: boolean,
  isCASIssued: boolean,

  enrolmentStatus: EnrolmentStatusEnum,
  isEnrolmentFormHidden: boolean,

  isScheduledInterviewsHidden: boolean,
  isScheduledInterviewsNew: boolean,
  isScheduledInterviewsComplete: boolean,
  sections: ISectionProps[],
  casBypass: boolean,
  currentStage?: CoreStatusExtended
): Array<ISectionProps> => {
  const update: { [key: string]: Partial<ISectionProps> } = {
    [SectionId.CHECKLIST]: {},
    [SectionId.GENERAL_DOC]: {},
    [SectionId.FINANCE_DOC]: {},
    [SectionId.PRE_CAS_INTERVIEW]: {},
    [SectionId.VISA_PREP]: {},
    [SectionId.ENROLMENT]: {},
    [SectionId.PRE_DEPARTURE_DOCS]: {},
    [SectionId.AFTER_TRAVEL_DOCS]: {},
    [SectionId.SCHEDULED_INTERVIEWS]: {}
  };

  const lockedSectionDescription = 'Your details are being processed and you can no longer edit this section';

  const lockChecklist = () => {
    if (isChecklistComplete) {
      update[SectionId.CHECKLIST].status = Status.Locked;
      update[SectionId.CHECKLIST].description = lockedSectionDescription;
    }
  };

  const lockMainSections = () => {
    lockChecklist();
    if (isChecklistComplete && isScheduledInterviewsComplete) {
      update[SectionId.SCHEDULED_INTERVIEWS].status = Status.Locked;
      update[SectionId.SCHEDULED_INTERVIEWS].description = lockedSectionDescription;
    }
    // lock general document section
    if (isChecklistComplete && isDocSectionComplete) {
      update[SectionId.GENERAL_DOC].status = Status.Locked;
      update[SectionId.GENERAL_DOC].description = lockedSectionDescription;
    }
    // lock finance documents section
    if (isChecklistComplete && isFinanceDocComplete) {
      update[SectionId.FINANCE_DOC].status = Status.Locked;
      update[SectionId.FINANCE_DOC].description = lockedSectionDescription;
    }
  };

  const lockSections = (stage: CoreStatusExtended) => {
    const stageMap: { [key in CoreStatusExtended]?: () => void } = {
      [CoreStatusExtended.PATHWAY_REVIEW]: lockChecklist,
      [CoreStatusExtended.READY_FOR_CAS]: lockChecklist,
      [CoreStatusExtended.CAS_ISSUED]: lockChecklist,
      [CoreStatusExtended.VISA_ISSUED]: lockMainSections,
      [CoreStatusExtended.VISA_REFUSED]: lockMainSections
    };
    const lockFunc = stageMap[stage];
    if (lockFunc) lockFunc();
  };

  // Checklist
  if (isChecklistComplete) {
    update[SectionId.CHECKLIST].status = Status.Completed;
  } else if (isChecklistInProgress) {
    update[SectionId.CHECKLIST].status = Status.InProgress;
  } else {
    update[SectionId.CHECKLIST].status = Status.Available;
  }

  // General Documents

  if (isChecklistComplete) {
    if (isDocSectionComplete) {
      update[SectionId.GENERAL_DOC].status = Status.Completed;
    } else if (isDocSectionInProgress) {
      update[SectionId.GENERAL_DOC].status = Status.InProgress;
    } else {
      update[SectionId.GENERAL_DOC].status = Status.Available;
    }
  } else {
    update[SectionId.GENERAL_DOC].status = Status.Disabled;
  }

  if (isChecklistComplete) {
    update[SectionId.GENERAL_DOC].clickable = true;
  }

  // Finance Documents
  if (isChecklistComplete) {
    if (isFinanceDocComplete) {
      update[SectionId.FINANCE_DOC].status = Status.Completed;
    } else if (isFinanceDocInvalid) {
      update[SectionId.FINANCE_DOC].status = Status.Invalid;
    } else if (isFinanceDocInProgress) {
      update[SectionId.FINANCE_DOC].status = Status.InProgress;
    } else {
      update[SectionId.FINANCE_DOC].status = Status.Available;
    }
  } else {
    update[SectionId.FINANCE_DOC].status = Status.Disabled;
  }

  if (isChecklistComplete) {
    update[SectionId.FINANCE_DOC].clickable = true;
  }

  if (isChecklistComplete) {
    update[SectionId.FINANCE_DOC].hidden = false;
  }

  // Pre-CAS Questionnaire

  if (isChecklistComplete && !isPreCasFormHidden) {
    if (isPreCasInterviewComplete) {
      update[SectionId.PRE_CAS_INTERVIEW].status = Status.Locked;
    } else if (isPreCasInterviewStarted) {
      update[SectionId.PRE_CAS_INTERVIEW].status = Status.InProgress;
    } else {
      update[SectionId.PRE_CAS_INTERVIEW].status = Status.Available;
    }
  } else {
    update[SectionId.PRE_CAS_INTERVIEW].status = Status.Disabled;
  }

  if (isChecklistComplete) {
    update[SectionId.PRE_CAS_INTERVIEW].clickable = true;
  }

  update[SectionId.PRE_CAS_INTERVIEW].hidden = isPreCasFormHidden;

  if (isChecklistComplete) {
    update[SectionId.SCHEDULED_INTERVIEWS].clickable = true;
  }

  // Scheduled Interviews

  if (isChecklistComplete) {
    update[SectionId.SCHEDULED_INTERVIEWS].clickable = true;

    if (isScheduledInterviewsComplete) {
      update[SectionId.SCHEDULED_INTERVIEWS].status = Status.Completed;
    } else if (isScheduledInterviewsNew) {
      update[SectionId.SCHEDULED_INTERVIEWS].status = Status.Available;
    } else {
      update[SectionId.SCHEDULED_INTERVIEWS].status = Status.InProgress;
    }
  } else {
    update[SectionId.SCHEDULED_INTERVIEWS].status = Status.Disabled;
  }
  update[SectionId.SCHEDULED_INTERVIEWS].hidden = isScheduledInterviewsHidden;

  // Enrolment status
  if (!isEnrolmentFormHidden) {
    update[SectionId.ENROLMENT].status = enrolmentStatus;
  }

  if (isChecklistComplete) {
    update[SectionId.ENROLMENT].clickable = true;
  }
  update[SectionId.ENROLMENT].hidden = isEnrolmentFormHidden;
  // END Enrolment status

  // Follow Up Documents

  if (isChecklistComplete) {
    update[SectionId.PRE_DEPARTURE_DOCS].clickable = true;
    update[SectionId.AFTER_TRAVEL_DOCS].clickable = true;

    if (isBeforeTravelDocsComplete) {
      update[SectionId.PRE_DEPARTURE_DOCS].status = Status.Completed;
    } else if (isBeforeTravelDocsInProgress) {
      update[SectionId.PRE_DEPARTURE_DOCS].status = Status.InProgress;
    } else {
      update[SectionId.PRE_DEPARTURE_DOCS].status = Status.Available;
    }

    if (isAfterArrivalDocsComplete) {
      update[SectionId.AFTER_TRAVEL_DOCS].status = Status.Completed;
    } else if (isAfterArrivalDocsInProgress) {
      update[SectionId.AFTER_TRAVEL_DOCS].status = Status.InProgress;
    } else {
      update[SectionId.AFTER_TRAVEL_DOCS].status = Status.Available;
    }
  } else {
    update[SectionId.PRE_DEPARTURE_DOCS].status = Status.Disabled;
    update[SectionId.AFTER_TRAVEL_DOCS].status = Status.Disabled;
  }

  update[SectionId.PRE_DEPARTURE_DOCS].hidden = isPreDepartureDocSectionHidden;
  update[SectionId.AFTER_TRAVEL_DOCS].hidden = isAfterTravelDocSectionHidden;

  // Visa Prep
  if (!isVisaPrepFormActivated) {
    update[SectionId.VISA_PREP].status = Status.Disabled;
  } else if (isChecklistComplete) {
    if (isVisaPrepFormComplete) {
      update[SectionId.VISA_PREP].status = Status.Completed;
    } else if (isVisaPrepFormStarted) {
      update[SectionId.VISA_PREP].status = Status.InProgress;
    } else {
      update[SectionId.VISA_PREP].status = Status.Available;
    }
  } else {
    update[SectionId.VISA_PREP].status = Status.Available;
  }

  if (isVisaPrepFormComplete) {
    update[SectionId.VISA_PREP].label = 'Draft Visa Application';

    update[SectionId.VISA_PREP].description = '';
  } else {
    update[SectionId.VISA_PREP].label = 'Start Draft Visa Application';
    update[SectionId.VISA_PREP].description = 'Begin the visa and immigration process';
  }

  update[SectionId.VISA_PREP].clickable = isVisaPrepFormActivated;

  update[SectionId.VISA_PREP].hidden = isVisaPrepFormHidden;

  update[SectionId.GENERAL_DOC].hidden = isGeneralDocFormHidden;
  update[SectionId.FINANCE_DOC].hidden = isFinanceDocFormHidden;
  update[SectionId.CHECKLIST].hidden = isChecklistHidden;

  if (currentStage) {
    lockSections(currentStage);
  }

  return sections.map(section => ({ ...section, ...update[section.id] })).filter(section => section.hidden !== true);
};
